import { useState } from "react";
import { RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/util";
import { usePermissions } from "@/lib/permissions";
import { LicenseBadge, fmtDate } from "@/components/security/client/LicenseBadge";

export const DURATIONS = [12, 24, 36];

export function DurationPicker({ value, onChange, prefix }) {
  return (
    <div className="flex gap-1 border border-border p-1">
      {DURATIONS.map(m => (
        <button key={m} type="button" onClick={() => onChange(m)}
                className={cn("font-mono text-[10px] tracking-widest uppercase px-3 py-1 flex-1",
                  value === m ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground")}
                data-testid={`${prefix}-${m}`}>{m} meses</button>
      ))}
    </div>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="font-mono text-sm mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}

export function LicensePanel({ client, onRenewed }) {
  const { isAdmin } = usePermissions();
  const [months, setMonths] = useState(12);
  const [busy, setBusy] = useState(false);

  const renew = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${client.id}/license/renew`, { months });
      toast.success(`Licencia renovada hasta ${fmtDate(r.data.license_end_date)}`);
      onRenewed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo renovar la licencia");
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-border bg-card/60 p-5 space-y-4" data-testid="license-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="overline">Licencia</div>
        <LicenseBadge client={client} testid="license-panel-badge" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Info label="Inicio" value={fmtDate(client.license_start_date)} testid="license-start" />
        <Info label="Vencimiento" value={fmtDate(client.license_end_date)} testid="license-end" />
        <Info label="Duración" value={client.license_duration_months ? `${client.license_duration_months} meses` : "—"} testid="license-duration" />
        <Info label="Última renovación" value={fmtDate(client.last_renewal_date)} testid="license-last-renewal" />
      </div>
      {isAdmin && (
        <div className="flex flex-col md:flex-row md:items-center gap-3 pt-2 border-t border-border">
          <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Renovar por</span>
          <div className="md:w-80"><DurationPicker value={months} onChange={setMonths} prefix="renew-months" /></div>
          <button onClick={renew} disabled={busy}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="license-renew-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Renovar licencia
          </button>
          <span className="text-xs text-muted-foreground">Se suma desde el vencimiento actual (o desde hoy si ya venció).</span>
        </div>
      )}
    </section>
  );
}
